import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import MicrolenderMonthlyManagement from './MicrolenderMonthlyManagement';
import NamfisaXmlView from '../components/NamfisaXmlView';
import { zipSync, strToU8 } from 'fflate';
import { toCurrency } from '../utils/adminMetrics';
import { formatDateTimeDMY } from '../utils/dateUtils';
import { sortRows, makeSortTh, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from '../components/UI/tableSort';
import { cardClass } from '../components/UI/dashboardCard';
import PageTabs from '../components/UI/PageTabs';

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan – Mar)' },
  { value: 2, label: 'Q2 (Apr – Jun)' },
  { value: 3, label: 'Q3 (Jul – Sep)' },
  { value: 4, label: 'Q4 (Oct – Dec)' },
];

function buildYearOptions() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current + 1; y >= current - 6; y -= 1) years.push(y);
  return years;
}

function formatNumber(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Whole-number formatter for 'number' (count) fields — no decimals, no N$.
function formatCount(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// The value the XML export writes for a field or matrix cell: the entered
// value, or the suggestion when nothing was entered (the same rule as the
// API's buildReturnXml `pick`). Totals use it so they match the downloaded XML.
const isBlank = (v) => v === null || v === undefined || v === '';
function exportValue(entered, suggested) {
  if (!isBlank(entered)) return entered;
  return isBlank(suggested) ? null : suggested;
}

// Format a suggested value for display according to the field type:
// currency → "N$1,234.56", percent → "12.00%", number → "1,234".
function formatSuggestion(field, v) {
  if (field.type === 'currency') return toCurrency(v);
  if (field.type === 'percent') return `${formatNumber(v)}%`;
  return formatCount(v);
}

// Row, column and grand totals of a grid, from POST /api/reports/grid-totals.
// Refreshed (debounced) whenever the grid's values change.
// gridRows: [{ key, values: { [column]: number } }]
function useGridTotals(gridRows, columns, enabled = true) {
  const [totals, setTotals] = useState(null);
  const [error, setError] = useState('');
  const signature = JSON.stringify([gridRows, columns]);
  useEffect(() => {
    if (!enabled) { setTotals(null); setError(''); return undefined; }
    const [rows, cols] = JSON.parse(signature);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(
          apiUrl('/api/reports/grid-totals'),
          { rows, columns: cols },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } },
        );
        if (!cancelled) { setTotals(res.data); setError(''); }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || 'Could not work out the totals.');
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [signature, enabled]);
  return { totals, error };
}

// The last quarter that has ended: the returns are made for it (the API
// refuses a quarter that has not ended).
const lastCompletedQuarter = (d = new Date()) => {
  const current = Math.floor(d.getMonth() / 3) + 1;
  return current === 1 ? { year: d.getFullYear() - 1, quarter: 4 } : { year: d.getFullYear(), quarter: current - 1 };
};

// The returns against the monthly management report for the same quarter
// (GET /api/namfisa/reports/:year/:quarter/reconciliation).
const ReconciliationPanel = ({ year, quarter, authHeader, refreshKey }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // A click on a column header sorts the checks.
  const [checkSort, setCheckSort] = useState(null);
  const checkTh = makeSortTh(checkSort, setCheckSort);
  const ch = { className: `px-2 py-1.5 ${TABLE_HEAD_TEXT}` };
  const chRight = { ...ch, align: 'right' };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(apiUrl(`/api/namfisa/reports/${year}/${quarter}/reconciliation`), { headers: authHeader });
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.error || 'The reconciliation could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [year, quarter, authHeader]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const fmt = (c, v) => (c.unit === 'count' ? String(v) : toCurrency(v));
  const differing = (data?.checks || []).filter((c) => !c.ok).length;
  const p = data?.portalRollForward;

  return (
    <div className={`${cardClass('blue')} mb-3`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-left">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Reconciliation with the management report</h3>
          <p className="text-[11px] text-gray-500 dark:text-slate-400">
            {loading ? 'Checking…' : error ? error : data
              ? (differing ? `${differing} of ${data.checks.length} checks differ` : `All ${data.checks.length} checks agree (to ${toCurrency(Number(data.tolerance))})`)
              : ''}
          </p>
        </div>
        <span className={`text-xs font-semibold ${differing ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-400'}`}>
          {data ? (differing ? `${differing} to check` : '✓') : ''} <span className="font-normal text-gray-500 dark:text-slate-400 ml-1">{open ? 'Hide' : 'Show'}</span>
        </span>
      </button>
      {open && data && (
        <div className="px-4 pb-4">
          <table className="w-full text-xs">
            <thead>
              <tr className={TABLE_HEAD_ROW}>
                {checkTh('Check', 'check', (c) => c.label, ch)}
                {checkTh('Return', 'return', (c) => Number(c.namfisa), chRight)}
                {checkTh('Management report', 'management', (c) => Number(c.management), chRight)}
                {checkTh('Difference', 'difference', (c) => (c.ok ? 0 : Math.abs(Number(c.difference)) || 0), chRight)}
              </tr>
            </thead>
            <tbody>
              {sortRows(data.checks, checkSort).map((c) => (
                <tr key={c.label} className={TABLE_ROW_LINE}>
                  <td className="px-2 py-1.5 text-gray-800 dark:text-slate-200">{c.label}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmt(c, c.namfisa)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmt(c, c.management)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {c.ok
                      ? <span className="text-emerald-700 dark:text-emerald-400 font-semibold">✓</span>
                      : <span className="text-amber-700 dark:text-amber-300 font-semibold">{fmt(c, c.difference)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {p && (
            <div className="mt-3 rounded-md border border-gray-200 dark:border-slate-700 p-3 text-xs text-gray-700 dark:text-slate-300">
              <div className="font-semibold text-gray-800 dark:text-slate-100">Portal roll-forward</div>
              <div className="text-[11px] text-gray-500 dark:text-slate-400">{p.formula}</div>
              <div className="mt-1">
                Portal loan book at the end <b>{toCurrency(p.portal_loan_book_end)}</b> · management report net book <b>{toCurrency(p.management_net_book)}</b> · gap{' '}
                <b className={p.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}>{toCurrency(p.gap)}</b>
              </div>
              <ul className="mt-1 list-disc pl-4 text-[11px] text-gray-500 dark:text-slate-400">
                {p.explanation.map((e) => <li key={e.label}>{e.label}: {toCurrency(e.amount)}</li>)}
              </ul>
            </div>
          )}
          <button type="button" onClick={load} className="mt-2 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">Check again</button>
        </div>
      )}
    </div>
  );
};

const NamfisaReports = () => {
  const yearOptions = useMemo(buildYearOptions, []);
  const lastEnded = lastCompletedQuarter();
  const [year, setYear] = useState(lastEnded.year);
  const [quarter, setQuarter] = useState(lastEnded.quarter);
  const [reconcileKey, setReconcileKey] = useState(0);

  const [activeTab, setActiveTab] = useState('forms'); // 'forms' | 'management'
  const [submissions, setSubmissions] = useState([]); // [{ slug, name, forms: [{key, formName, ...}] }]
  const [selectedSubmissionSlug, setSelectedSubmissionSlug] = useState('');
  const [selectedFormKey, setSelectedFormKey] = useState('');

  const [report, setReport] = useState(null); // full report (all sections)
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type, text }
  const [recomputing, setRecomputing] = useState(false);
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  // How the selected return is laid out in NAMFISA's XML (header + where each
  // field goes); the form is shown and edited in that shape.
  const [xmlLayout, setXmlLayout] = useState(null);

  const authHeader = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` }),
    []
  );

  // Load the submission/forms catalogue once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(apiUrl('/api/namfisa/submissions'), { headers: authHeader });
        if (!alive) return;
        const list = res.data || [];
        setSubmissions(list);
        if (list.length && !selectedSubmissionSlug) {
          setSelectedSubmissionSlug(list[0].slug);
          if (list[0].forms?.length) setSelectedFormKey(list[0].forms[0].key);
        }
      } catch (err) {
        if (alive) setMessage({ type: 'error', text: 'Failed to load NAMFISA submission catalogue.' });
      }
    })();
    return () => { alive = false; };
  }, []); // intentionally fires once on mount

  // Whenever the user changes submission, default the form to the first one.
  useEffect(() => {
    if (!selectedSubmissionSlug) return;
    const sub = submissions.find((s) => s.slug === selectedSubmissionSlug);
    if (sub?.forms?.length && !sub.forms.some((f) => f.key === selectedFormKey)) {
      setSelectedFormKey(sub.forms[0].key);
    }
  }, [selectedSubmissionSlug, submissions, selectedFormKey]);

  // Load the full report (all sections with saved + suggested values).
  const loadReport = useCallback(async (y, q) => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await axios.get(apiUrl(`/api/namfisa/reports/${y}/${q}`), { headers: authHeader });
      setReport(res.data);
    } catch (err) {
      setReport(null);
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load NAMFISA report.' });
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { loadReport(year, quarter); }, [year, quarter, loadReport]);

  const selectedSubmission = submissions.find((s) => s.slug === selectedSubmissionSlug) || null;

  useEffect(() => {
    if (!selectedSubmissionSlug) { setXmlLayout(null); return undefined; }
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(
          apiUrl(`/api/namfisa/reports/${year}/${quarter}/xml-view/${selectedSubmissionSlug}`),
          { headers: authHeader }
        );
        if (alive) setXmlLayout(res.data || null);
      } catch {
        if (alive) setXmlLayout(null); // falls back to the plain form
      }
    })();
    return () => { alive = false; };
  }, [selectedSubmissionSlug, year, quarter, authHeader]);

  const fetchReturnXml = async (slug) => {
    const res = await axios.get(
      apiUrl(`/api/namfisa/reports/${year}/${quarter}/xml/${slug}`),
      { headers: authHeader, responseType: 'blob' }
    );
    const disposition = res.headers['content-disposition'] || '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: res.data, name: match ? match[1] : null };
  };
  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Every return for the quarter (SCI, SFP, AFI, NFI) in one ZIP.
  const downloadAllXml = async () => {
    if (!submissions.length) return;
    setDownloadingAll(true);
    setMessage(null);
    try {
      const files = {};
      for (const sub of submissions) {
        const { blob, name } = await fetchReturnXml(sub.slug);
        files[name || `${sub.name} ${year} Q${quarter}.xml`] = strToU8(await blob.text());
      }
      const zip = zipSync(files, { level: 6 });
      saveBlob(new Blob([zip], { type: 'application/zip' }), `NAMFISA quarterly returns ${year} Q${quarter}.zip`);
      setMessage({ type: 'success', text: `Downloaded ${Object.keys(files).length} XML files for ${year} Q${quarter}.` });
    } catch (err) {
      let text = 'The XML files could not be generated.';
      try { text = JSON.parse(await err.response?.data?.text())?.error || text; } catch { /* keep the default */ }
      setMessage({ type: 'error', text });
    } finally {
      setDownloadingAll(false);
    }
  };

  // The selected return as NAMFISA's XML (built by the API from its XSD, with
  // the saved values, or the suggested ones where nothing was saved).
  const downloadXml = async () => {
    if (!selectedSubmissionSlug) return;
    setDownloadingXml(true);
    setMessage(null);
    try {
      const { blob, name } = await fetchReturnXml(selectedSubmissionSlug);
      saveBlob(new Blob([blob], { type: 'application/xml' }), name || `${selectedSubmission?.name || 'NAMFISA return'} ${year} Q${quarter}.xml`);
    } catch (err) {
      let text = 'The XML could not be generated.';
      try { text = JSON.parse(await err.response?.data?.text())?.error || text; } catch { /* keep the default */ }
      setMessage({ type: 'error', text });
    } finally {
      setDownloadingXml(false);
    }
  };
  const selectedSection = report?.sections?.find((s) => s.section === selectedFormKey) || null;

  const updateSectionValue = (sectionKey, fieldKey, value) => {
    setReport((prev) => prev && ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.section === sectionKey ? { ...s, values: { ...s.values, [fieldKey]: value } } : s
      ),
    }));
  };

  const saveSection = async (section) => {
    setMessage(null);
    try {
      const res = await axios.put(
        apiUrl(`/api/namfisa/reports/${year}/${quarter}/sections/${section.section}`),
        section.values,
        { headers: authHeader }
      );
      setReport((prev) => prev && ({
        ...prev,
        sections: prev.sections.map((s) => (s.section === section.section ? res.data : s)),
      }));
      setMessage({ type: 'success', text: `Saved "${section.title}" (Part ${section.part}).` });
      setReconcileKey((k) => k + 1);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || `Failed to save ${section.title}.` });
    }
  };

  const recomputeSuggestions = async () => {
    if (!selectedSection) return;
    setRecomputing(true);
    try {
      const res = await axios.get(
        apiUrl(`/api/namfisa/reports/${year}/${quarter}/sections/${selectedSection.section}/suggestions`),
        { headers: authHeader }
      );
      const next = res.data?.suggestedValues || {};
      setReport((prev) => prev && ({
        ...prev,
        sections: prev.sections.map((s) =>
          s.section === selectedSection.section ? { ...s, suggestedValues: next } : s
        ),
      }));
      setMessage({ type: 'success', text: 'Suggestions refreshed from current loan data.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to recompute suggestions.' });
    } finally {
      setRecomputing(false);
    }
  };

  const applySuggestion = (fieldKey) => {
    if (!selectedSection) return;
    const suggested = selectedSection.suggestedValues?.[fieldKey];
    if (suggested == null) return;
    updateSectionValue(selectedSection.section, fieldKey, suggested);
  };

  return (
    <div className="max-w-full">

      {/* Top-level tabs */}
      <PageTabs
        tabs={[
          { key: 'forms', label: 'Quarterly Forms' },
          { key: 'management', label: 'Monthly Management Report' },
        ]}
        active={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'management' && (
        <MicrolenderMonthlyManagement />
      )}

      {activeTab === 'forms' && (
      <>
      {/* Period selector */}
      <div className={`${cardClass('blue')} p-4 pt-5 mb-3 flex flex-wrap items-end gap-3`}>
        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-slate-300 mb-1">Year</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            
          >
            {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-slate-300 mb-1">Quarter</label>
          <select
            value={quarter}
            onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
            
          >
            {QUARTERS.map((q) => (<option key={q.value} value={q.value}>{q.label}</option>))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => loadReport(year, quarter)}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white"
        >
          {loading ? 'Loading…' : 'Reload'}
        </button>
        {report?.period?.updated_at && (
          <span className="text-xs text-gray-500 dark:text-slate-400 ml-auto">
            Period last edited {formatDateTimeDMY(report.period.updated_at)}
          </span>
        )}
      </div>

      {/* Submission + Form pickers */}
      <div className={`${cardClass('blue')} p-4 pt-5 mb-3 flex flex-wrap items-end gap-3`}>
        <div className="min-w-[260px]">
          <label className="block text-xs font-semibold text-gray-600 dark:text-slate-300 mb-1">Submission</label>
          <select
            value={selectedSubmissionSlug}
            onChange={(e) => setSelectedSubmissionSlug(e.target.value)}
            className="min-w-[260px]"
          >
            {submissions.map((s) => (<option key={s.slug} value={s.slug}>{s.name}</option>))}
          </select>
        </div>
        <div className="min-w-[260px]">
          <label className="block text-xs font-semibold text-gray-600 dark:text-slate-300 mb-1">Form</label>
          <select
            value={selectedFormKey}
            onChange={(e) => setSelectedFormKey(e.target.value)}
            className="min-w-[260px]"
            disabled={!selectedSubmission?.forms?.length}
          >
            {(selectedSubmission?.forms || []).map((f) => (
              <option key={f.key} value={f.key}>{f.formName}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex flex-col items-end">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={downloadXml}
              disabled={!selectedSubmissionSlug || downloadingXml}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold"
            >
              {downloadingXml ? 'Generating…' : `Download XML — ${selectedSubmission?.name || 'this return'}`}
            </button>
            <button
              type="button"
              onClick={downloadAllXml}
              disabled={!submissions.length || downloadingAll}
              className="px-4 py-2 rounded-lg border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-700 disabled:opacity-50 text-sm font-semibold"
              title="All the quarterly returns (SCI, SFP, AFI, NFI) as XML files in one ZIP"
            >
              {downloadingAll ? 'Generating…' : 'Download all XML files'}
            </button>
          </div>
          <span className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
            {year} Q{quarter}, in NAMFISA's XML format — all returns come as one ZIP
          </span>
        </div>
      </div>

      {message && (
        <div
          className={`mb-3 p-3 rounded border text-sm ${message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/40'
            : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/40'
          }`}
        >
          {message.text}
        </div>
      )}

      {report && <ReconciliationPanel year={year} quarter={quarter} authHeader={authHeader} refreshKey={reconcileKey} />}

      {loading && !report && (
        <div className="text-sm text-gray-500 dark:text-slate-400">Loading report…</div>
      )}

      {selectedSection && xmlLayout?.sections?.[selectedSection.section] && (
        <NamfisaXmlView
          layout={xmlLayout}
          section={selectedSection}
          onChange={(field, value) => updateSectionValue(selectedSection.section, field, value)}
          onSave={() => saveSection(selectedSection)}
          onRecompute={recomputeSuggestions}
          recomputing={recomputing}
        />
      )}

      {selectedSection && !xmlLayout && (
        <div className="mb-3 p-3 rounded border text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800/40">
          The XML view of this form could not be loaded — the server may need updating (restart the API). Showing the plain form meanwhile.
        </div>
      )}

      {selectedSection && !xmlLayout?.sections?.[selectedSection.section] && (
        <SectionPanel
          section={selectedSection}
          onChange={(field, value) => updateSectionValue(selectedSection.section, field, value)}
          onSave={() => saveSection(selectedSection)}
          onRecompute={recomputeSuggestions}
          onApplySuggestion={applySuggestion}
          recomputing={recomputing}
        />
      )}

      {!loading && !selectedSection && report && (
        <div className="text-sm text-gray-500 dark:text-slate-400 italic">Pick a form above to start.</div>
      )}
      </>
      )}
    </div>
  );
};

// Renders a 2D matrix field (rows × columns) as an editable grid. The value
// shape is { rowKey: { colKey: number|null } }. Computed matrices expose a
// suggested grid (same shape) used as cell placeholders + a "Use suggested
// values" button that applies the whole grid at once.
const MatrixField = ({ field, value, suggested, onChange, onApplySuggested }) => {
  const matrix = (value && typeof value === 'object') ? value : {};
  const suggestedMatrix = (suggested && typeof suggested === 'object') ? suggested : {};
  const hasSuggested = !!field.computed && suggested && typeof suggested === 'object';
  const cols = field.columns || [];
  const rows = field.rows || [];
  const showTotals = !!field.showTotals;
  const isInt = !!field.cellInteger;
  const step = isInt ? '1' : '0.01';
  const fmt = isInt ? formatCount : formatNumber;

  const setCell = (rowKey, colKey, cellVal) => {
    const next = {};
    for (const row of rows) {
      next[row.key] = { ...(matrix[row.key] || {}) };
    }
    if (!next[rowKey]) next[rowKey] = {};
    next[rowKey][colKey] = cellVal === '' ? null : cellVal;
    onChange(field.key, next);
  };

  // Live totals of the values the XML will carry (entered, else suggested),
  // worked out by the API.
  const { totals, error: totalsError } = useGridTotals(
    rows.map((r) => ({
      key: r.key,
      values: cols.reduce((acc, c) => ({
        ...acc,
        [c.key]: exportValue(matrix?.[r.key]?.[c.key], suggestedMatrix?.[r.key]?.[c.key]),
      }), {}),
    })),
    cols.map((c) => c.key),
    showTotals,
  );
  const rowTotal = (rowKey) => totals?.row_totals?.[rowKey] ?? 0;
  const colTotal = (colKey) => totals?.column_totals?.[colKey] ?? 0;
  const grandTotal = totals?.grand_total ?? 0;

  const cellCls = 'px-2 py-1 border border-gray-200 dark:border-slate-700 text-center';
  const totalCls = 'px-2 py-1 border border-gray-200 dark:border-slate-700 text-right font-semibold bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-200';

  return (
    <div className="overflow-x-auto">
      {hasSuggested && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => onApplySuggested(field.key)}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700/50 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            title="Fill the whole grid with values suggested from current loan data"
          >
            Use suggested values
          </button>
        </div>
      )}
      <table className="min-w-full text-xs border border-gray-200 dark:border-slate-700">
        <thead>
          <tr className="bg-gray-100 dark:bg-slate-800">
            <th className={cellCls} rowSpan={2} />
            {showTotals && (
              <th
                rowSpan={2}
                className={`${cellCls} font-semibold text-gray-700 dark:text-slate-200 whitespace-nowrap`}
              >
                {field.totalColumnHeader || 'Total'}
              </th>
            )}
            <th
              colSpan={cols.length}
              className="px-2 py-1 border border-gray-200 dark:border-slate-700 text-center font-semibold text-gray-700 dark:text-slate-200"
            >
              {field.columnsHeader || ''}
            </th>
          </tr>
          <tr className="bg-gray-50 dark:bg-slate-900/40">
            {cols.map((col) => (
              <th key={col.key} className={`${cellCls} font-semibold text-gray-700 dark:text-slate-200 whitespace-nowrap`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className={`${cellCls} text-left font-semibold text-gray-800 dark:text-slate-100 whitespace-nowrap`}>
                {row.label}
                {row.required && <span className="text-red-500"> *</span>}
              </td>
              {showTotals && (
                <td className={totalCls}>{fmt(rowTotal(row.key))}</td>
              )}
              {cols.map((col) => {
                const cell = matrix?.[row.key]?.[col.key];
                const sCell = hasSuggested ? suggested?.[row.key]?.[col.key] : null;
                return (
                  <td key={col.key} className={cellCls}>
                    <input
                      type="number"
                      step={step}
                      value={cell == null ? '' : String(cell)}
                      onChange={(e) => setCell(row.key, col.key, e.target.value)}
                      placeholder={sCell != null ? fmt(sCell) : '0'}
                      className="w-28 text-right"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
          {showTotals && (
            <tr className="bg-gray-100 dark:bg-slate-800">
              <td className={`${cellCls} text-left font-bold text-gray-800 dark:text-slate-100`}>
                {field.totalsLabel || 'Total'}
              </td>
              <td className={totalCls}>{fmt(grandTotal)}</td>
              {cols.map((col) => (
                <td key={col.key} className={totalCls}>{fmt(colTotal(col.key))}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
      {totalsError && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{totalsError}</p>
      )}
    </div>
  );
};

const SectionPanel = ({ section, onChange, onSave, onRecompute, onApplySuggestion, recomputing }) => {
  const hasComputed = section.fields.some((f) => f.computed);

  // All NAMFISA sections (the gray-bar groups inside a form) start collapsed.
  // Clicking the header toggles. Switching forms resets the set.
  const allGroupKeys = useMemo(() => {
    const seen = [];
    for (const f of section.fields) {
      const g = f.group || '__ungrouped__';
      if (!seen.includes(g)) seen.push(g);
    }
    return seen;
  }, [section.fields]);

  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set(allGroupKeys));
  useEffect(() => {
    // When the form changes (different section.section), collapse everything.
    setCollapsedGroups(new Set(allGroupKeys));
  }, [section.section, allGroupKeys]);

  const toggleGroup = (group) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      const key = group || '__ungrouped__';
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Field count per group (for the "(5 fields)" hint on collapsed headers).
  const groupCounts = useMemo(() => {
    const map = {};
    for (const f of section.fields) {
      const g = f.group || '__ungrouped__';
      map[g] = (map[g] || 0) + 1;
    }
    return map;
  }, [section.fields]);

  // Numeric-field info per group, for the optional section-total shown in the
  // group header (only when section.showGroupTotals and the group has 2+
  // numeric fields). A total only adds like fields: groups that mix units
  // (amounts with counts or percentages) get none, and percentages are never
  // summed. unit is 'currency' | 'number' | 'percent' | 'mixed'.
  const numericGroupInfo = useMemo(() => {
    const info = {};
    for (const f of section.fields) {
      if (!['number', 'currency', 'percent'].includes(f.type)) continue;
      const g = f.group || '__ungrouped__';
      if (!info[g]) info[g] = { keys: [], unit: f.type };
      info[g].keys.push(f.key);
      if (info[g].unit !== f.type) info[g].unit = 'mixed';
    }
    return info;
  }, [section.fields]);
  const isTotallable = (inf) => !!inf && inf.keys.length >= 2 && (inf.unit === 'currency' || inf.unit === 'number');

  // Group totals of the values the XML will carry (entered, else suggested),
  // worked out by the API (one grid row per summable group).
  const { totals: groupTotals, error: groupTotalsError } = useGridTotals(
    Object.entries(numericGroupInfo).filter(([, inf]) => isTotallable(inf)).map(([g, inf]) => ({
      key: g,
      values: inf.keys.reduce((acc, k) => ({
        ...acc,
        [k]: exportValue(section.values[k], section.suggestedValues?.[k]),
      }), {}),
    })),
    [],
    !!section.showGroupTotals,
  );
  const groupTotal = (groupKey) => groupTotals?.row_totals?.[groupKey] ?? 0;

  return (
    <div className={cardClass('blue')}>
      <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold text-blue-800 dark:text-blue-300">
            Part {section.part} · {section.title}
          </h2>
          {section.description && (
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{section.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasComputed && (
            <button
              type="button"
              onClick={onRecompute}
              disabled={recomputing}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700/50 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50"
              title="Refresh suggested values from current loan/repayment data"
            >
              {recomputing ? 'Recomputing…' : 'Recompute from data'}
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            className="px-4 py-1.5 rounded-md text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white"
          >
            Save section
          </button>
        </div>
      </div>
      {groupTotalsError && (
        <p className="px-4 pt-2 text-xs text-red-600 dark:text-red-400">{groupTotalsError}</p>
      )}

      <table className="w-full text-sm">
        <tbody>
          {section.fields.map((field, idx) => {
            const isNumeric = ['number', 'currency', 'percent'].includes(field.type);
            const rawValue = section.values[field.key];
            const numericDisplay = rawValue == null ? '' : String(rawValue);
            const suggested = section.suggestedValues?.[field.key];
            const hasSuggestion = field.computed && suggested != null && Number.isFinite(Number(suggested));
            const userMatchesSuggestion = hasSuggestion && Number(rawValue) === Number(suggested);
            const userBlank = rawValue == null || rawValue === '' || Number.isNaN(Number(rawValue));
            // Show a group header row when this field's group changes from
            // the previous one (treats no-group as its own "Other" bucket).
            const prevGroup = idx === 0 ? null : (section.fields[idx - 1].group || null);
            const showGroupHeader = (field.group || null) !== prevGroup && field.group;
            const groupKey = field.group || '__ungrouped__';
            const isCollapsed = collapsedGroups.has(groupKey);
            return (
              <React.Fragment key={field.key}>
                {showGroupHeader && (
                  <tr className="bg-gray-300 dark:bg-slate-700">
                    <td
                      colSpan={2}
                      onClick={() => toggleGroup(field.group)}
                      className="px-4 py-2 text-sm font-bold text-gray-900 dark:text-slate-100 border-t border-gray-400 dark:border-slate-600 cursor-pointer select-none hover:bg-gray-400/60 dark:hover:bg-slate-600 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span>
                          <span className="inline-block w-3 mr-2 text-gray-700 dark:text-slate-300">
                            {isCollapsed ? '▶' : '▼'}
                          </span>
                          {field.group}
                          <span className="ml-2 text-xs font-normal text-gray-600 dark:text-slate-400">
                            ({groupCounts[groupKey]} {groupCounts[groupKey] === 1 ? 'field' : 'fields'})
                          </span>
                        </span>
                        {section.showGroupTotals && isTotallable(numericGroupInfo[groupKey]) && (
                          <span className="font-mono font-bold text-gray-900 dark:text-slate-100">
                            {numericGroupInfo[groupKey].unit === 'currency'
                              ? toCurrency(groupTotal(groupKey))
                              : formatCount(groupTotal(groupKey))}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {field.type === 'matrix' && !isCollapsed && (
                  <tr className="bg-white dark:bg-slate-800">
                    <td colSpan={2} className="px-4 py-3 border-t border-gray-100 dark:border-slate-700">
                      <MatrixField
                        field={field}
                        value={section.values[field.key]}
                        suggested={section.suggestedValues?.[field.key]}
                        onChange={onChange}
                        onApplySuggested={onApplySuggestion}
                      />
                      {field.computedNote && (
                        <div className="mt-2 text-[11px] italic text-gray-400 dark:text-slate-500">
                          {field.computedNote}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                {field.type !== 'matrix' && !isCollapsed && (
                  <tr className={idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900/40'}>
                    <td className="px-4 py-3 font-semibold text-gray-800 dark:text-slate-100 w-2/3 border-t border-gray-100 dark:border-slate-700 align-top">
                      <div>
                        {field.label}
                        {field.displayOnly && (
                          <span className="ml-2 rounded-full border border-amber-300 dark:border-amber-700 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-300">Type into the portal</span>
                        )}
                      </div>
                      {hasSuggestion && (
                        <div className="mt-1 text-[11px] font-normal text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-2">
                          <span>
                            Suggested from data: <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">{formatSuggestion(field, suggested)}</span>
                          </span>
                          {!userMatchesSuggestion && (
                            <button
                              type="button"
                              onClick={() => onApplySuggestion(field.key)}
                              className="text-blue-700 dark:text-blue-300 hover:underline font-semibold"
                            >
                              {userBlank ? 'Use this' : 'Replace with suggested'}
                            </button>
                          )}
                          {userMatchesSuggestion && (
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">in sync</span>
                          )}
                        </div>
                      )}
                      {field.computedNote && (
                        <div className="mt-0.5 text-[11px] italic text-gray-400 dark:text-slate-500">
                          {field.computedNote}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 border-t border-gray-100 dark:border-slate-700 align-top">
                      <div className="flex items-center justify-end gap-1">
                        <input
                          type={isNumeric ? 'number' : 'text'}
                          step={field.type === 'percent' ? '0.01' : '0.01'}
                          value={isNumeric ? numericDisplay : (rawValue ?? '')}
                          onChange={(e) => onChange(field.key, e.target.value)}
                          className="w-40 sm:w-48 text-right"
                          placeholder={isNumeric ? (hasSuggestion ? (field.type === 'number' ? formatCount(suggested) : formatNumber(suggested)) : '0') : ''}
                        />
                        {field.required && <span className="text-red-500 text-base leading-none">*</span>}
                      </div>
                    </td>
                  </tr>
                )}
                {!isCollapsed && field.specifyKey && (() => {
                  const specifySuggested = section.suggestedValues?.[field.specifyKey];
                  const hasSpecifySuggestion = typeof specifySuggested === 'string' && specifySuggested.trim() !== '';
                  const specifyValue = section.values[field.specifyKey] ?? '';
                  const specifyInSync = hasSpecifySuggestion && String(specifyValue).trim() === specifySuggested.trim();
                  return (
                  <tr className={idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900/40'}>
                    <td className="px-4 py-3 font-semibold text-gray-800 dark:text-slate-100 w-2/3 border-t border-gray-100 dark:border-slate-700 align-top">
                      <div>{field.specifyLabel || 'Please specify'}</div>
                      {hasSpecifySuggestion && (
                        <div className="mt-1 text-[11px] font-normal text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-2">
                          <span>
                            Suggested from data: <span className="font-semibold text-blue-700 dark:text-blue-300">{specifySuggested}</span>
                          </span>
                          {!specifyInSync ? (
                            <button
                              type="button"
                              onClick={() => onApplySuggestion(field.specifyKey)}
                              className="text-blue-700 dark:text-blue-300 hover:underline font-semibold"
                            >
                              {String(specifyValue).trim() === '' ? 'Use this' : 'Replace with suggested'}
                            </button>
                          ) : (
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">in sync</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 border-t border-gray-100 dark:border-slate-700 align-top">
                      <div className="flex items-center justify-end gap-1">
                        <input
                          type="text"
                          value={specifyValue}
                          onChange={(e) => onChange(field.specifyKey, e.target.value)}
                          placeholder={hasSpecifySuggestion ? specifySuggested : ''}
                          className="w-40 sm:w-48 text-right"
                        />
                      </div>
                    </td>
                  </tr>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default NamfisaReports;
